import gql from 'graphql-tag';

import COULD_NOT_COMPLETE_FRAGMENT from '../../../common/graphql/serviceDocket/COULD_NOT_COMPLETE_FRAGMENT';

// Stop fields the driver day step builders (driverDaySteps.js) read.
export const DRIVER_MY_DAY_DOCKET_FRAGMENT = gql`
	fragment driverMyDayDocket on ServiceDocket {
		id
		totalPickItemsQuantity
		serviceNote
		...stopCouldNotComplete
		status
		date
		order
		manualRestock
		startedProcessing
		inventoryCheck
		hasSomeExpiryDatesToUpdate
		productExpiryCheckSchedules {
			id
		}
		replacementReviewRequired
		replacementReviewReason
		isIngredientRestock
		inventoryPicked
		inventoryPartiallyPicked
		pickListGenerationStatus
		salesSinceLastRestock
		isRestocked
		ticketsResolved
		machineRestockType
		cashBag
		tub
		collectCash
		cashCollected
		requirePhoto
		photoTaken
		machineServiceType
		packTags {
			id
			labelPrintOnly
		}
		driverTicketCount
		pickList {
			id
			isAnyItemPicked
			hasProductSoldSinceLastPickListRecalculation
			calculatedAt
		}
		# A separate cache key keeps docket-scoped items apart from the shared PickList id.
		productChangesPickList: pickList @connection(key: "driverProductChangesPickList") {
			pickListItems {
				id
				selection
				quantity
				extraQuantity
				swapQuantity
				removedProductQuantity
				planogramItemQuantity
				product {
					id
					name
				}
				removedProduct {
					id
					name
				}
			}
		}
		ticketList {
			id
			driverItems {
				id
				notResolved
				notResolvedComment
				ticket {
					id
					status
					visibleToDrivers
					description
				}
			}
		}
		machine {
			id
			type
			isOffline
			hasCoinMech
			usesIngredients
			assetId
			lastCommunication
			receivedCommunicationTypes
			temperatureCelcius
			overrideCoinLimits
			coinLimits
			accessHours
			directions
			coordinates
			noteCount
			useLocationCoordinates
			importantNotes {
				id
				description
			}
			machineModel {
				id
				name
			}
			machineType {
				id
				name
			}
			machineKey {
				id
				name
			}
			place {
				id
				name
				destroyed
				directions
				requiresAccessPass
				accessPassDetails
				requiredEquipment
				visualDirectionPhotos {
					id
					link
					comment
				}
				visualDirectionPhotoUrls
			}
			coinMech {
				id
				settings
			}
			noteReader {
				id
			}
			coinMechDetail {
				tubesValueTotal
				tubesDetail {
					coinTypeNumber
					valueCoinsInTube
					numberCoinsInTube
					numberCoinsInsertedManually
					numberCoinsDispensedManually
					tubeIsFull
				}
			}
			wrap {
				name
			}
			location {
				id
				name
				requiresAccessPass
				requiresSignIn
				signInInstructions
				accessPassDetails
				requiredEquipment
				accessHours
				directions
				visualDirectionPhotos {
					id
					link
					comment
				}
				visualDirectionPhotoUrls
				coordinates
				noteCount
				importantNotes {
					id
					description
				}
				addressObj {
					id
					region
					street
					city
					postcode
				}
			}
			commDevices {
				id
				deviceId
			}
		}
	}
	${COULD_NOT_COMPLETE_FRAGMENT}
`;

export const GET_DRIVER_MY_DAY_SERVICE_RUN = gql`
	query(
		$page: Int!,
		$perPage: Int!,
		$sortBy: String!,
		$sortDirection: Int!,
		$includeTrainingContext: Boolean!,
		$filter: ServiceRunPageFilter
	) {
		getServiceRuns(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				id
				planned
				isOneCashBag
				brief
				briefAcknowledgedAt
				vehicleConfirmed
				machineOrder
				myDayConfirmations {
					key
					at
					items
				}
				driverClockSummary @include(if: $includeTrainingContext) {
					clockedInAt
					clockedOutAt
					returnedToWarehouseAt
					reminderDueAt
					reminderResolvedAt
					isClockedIn
				}
				checklistSubmissions @include(if: $includeTrainingContext) {
					id
					purpose
					status
					submittedAt
					approvedAt
					approvedBy {
						id
						firstname
						lastname
					}
					signOffManager {
						id
						firstname
						lastname
					}
					signOffNote
				}
				route {
					id
					name
				}
				startLocation {
					id
					name
					coordinates
					addressObj {
						id
						region
						street
						city
						postcode
					}
				}
				endLocation {
					id
					name
					coordinates
					addressObj {
						id
						region
						street
						city
						postcode
					}
				}
				vehicle {
					id
					name
					registration
				}
				driver {
					id
					username
					firstname
					lastname
				}
				serviceDockets {
					...driverMyDayDocket
				}
			}
			count
		}
	}
	${DRIVER_MY_DAY_DOCKET_FRAGMENT}
`;

// One stop outside the loaded driver day (another day or driver, training,
// first load failed), with the run fields the stop actions need.
export const GET_DRIVER_DAY_SERVICE_DOCKET = gql`
	query($id: String) {
		getServiceDocket(id: $id) {
			...driverMyDayDocket
			serviceRun {
				id
				planned
				isOneCashBag
			}
		}
	}
	${DRIVER_MY_DAY_DOCKET_FRAGMENT}
`;

export const GET_DRIVER_MY_DAY_PICK_TOP_UP_STATUS = gql`
	query($id: String, $adminTraining: Boolean) {
		getServiceRun(id: $id) {
			id
			myDayPickTopUpStatus(adminTraining: $adminTraining) {
				canMarkNotNeeded
				markedNotNeeded
				picksUpToDate
			}
		}
	}
`;

// Kept apart from the board query so only the active run loads its pick list lines.
export const GET_DRIVER_MY_DAY_FRESH_FOOD = gql`
	query($id: String) {
		getServiceRun(id: $id) {
			id
			serviceDockets {
				id
				order
				freshBoxManufacturers {
					id
					name
				}
				machine {
					id
					assetId
					place {
						id
						name
					}
					location {
						id
						name
					}
				}
				# Repeat visits share one PickList id but get their own items, so keep them out of the PickList cache entry.
				freshFoodPickList: pickList @connection(key: "driverFreshFoodPickList") {
					pickListItems {
						id
						quantity
						extraQuantity
						product {
							id
							name
							isFreshFood
							manufacturer {
								id
								name
							}
						}
					}
				}
			}
		}
	}
`;

export const GET_MY_DAY_ASSIGNED_RUN_CHECK = gql`
	query(
		$sortBy: String!,
		$sortDirection: Int!,
		$filter: ServiceRunPageFilter
	) {
		getServiceRuns(
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				id
				planned
			}
			count
		}
	}
`;

export const GET_MY_DAY_EMPLOYEE_RUNS = gql`
	query getMyDayEmployeeRuns($filter: ServiceRunPageFilter) {
		getServiceRuns(
			sortBy: "planned"
			sortDirection: 1
			filter: $filter
		) {
			items {
				id
				planned
				route {
					id
					name
				}
				driver {
					id
					username
					firstname
					lastname
				}
			}
			count
		}
	}
`;

export const SUBSCRIBE_TO_SERVICE_DOCKET_MY_DAY = gql`
	subscription ($input: ServiceDocketSubscriptionInput!) {
		ServiceDocketSubscription(input: $input) {
			servicedocket {
				id
				totalPickItemsQuantity
				serviceNote
				status
				startedProcessing
				inventoryCheck
				hasSomeExpiryDatesToUpdate
				productExpiryCheckSchedules {
					id
				}
				replacementReviewRequired
				replacementReviewReason
				isIngredientRestock
				inventoryPicked
				inventoryPartiallyPicked
				pickListGenerationStatus
				isRestocked
				ticketsResolved
				requirePhoto
				photoTaken
				driverTicketCount
				pickList {
					id
					isAnyItemPicked
					hasProductSoldSinceLastPickListRecalculation
					calculatedAt
				}
			}
		}
	}
`;
